/**
 * Каталог для отбора.
 *
 * Собирает из локальной базы всё, что нужно фильтру: победившее значение по
 * каждому полю, имя поставщика, наличие отчёта. Работает без сети — на выставке
 * это обычное состояние, и отбор не должен ждать сервера.
 *
 * Читается один раз на вход в экран и держится в памяти. На нынешних объёмах
 * этого достаточно; когда камней станут тысячи, отбор переедет в SQLite/OPFS —
 * StoneRow специально сделан плоским, чтобы движок можно было подменить,
 * не переписывая экраны.
 */
import { db, type LocalStone } from '../db';
import { pictureUrl } from './pictures';
import { resolveCurrent, type Fact, type StoneRow } from '@core';
import { whereIs } from './where';

export async function loadRows(): Promise<StoneRow[]> {
  const [stones, facts, suppliers, certs, voices, places] = await Promise.all([
    db.stones.toArray(),
    db.facts.toArray(),
    db.suppliers.toArray(),
    db.certs.toArray(),
    db.voices.toArray(),
    db.exhibitions.toArray(),
  ]);
  // Валюта места — умолчание для камней, у которых своя не названа.
  const currencyByPlace = new Map(places.map((p) => [p.id, p.currency || 'USD']));

  const byStone = new Map<string, Fact[]>();
  for (const f of facts) {
    const list = byStone.get(f.subjectId) ?? [];
    list.push({ ...f, subjectType: 'stone', supersededBy: null } as unknown as Fact);
    byStone.set(f.subjectId, list);
  }

  const supplierById = new Map(suppliers.map((s) => [s.id, s]));

  const certsByStone = new Map<string, { any: boolean; verified: boolean }>();
  for (const c of certs) {
    const prev = certsByStone.get(c.stoneId) ?? { any: false, verified: false };
    certsByStone.set(c.stoneId, {
      any: true,
      verified: prev.verified || Boolean(c.verifiedAt),
    });
  }

  const noteByStone = new Map<string, string>();
  for (const v of voices) {
    if (v.transcript && !noteByStone.has(v.subjectId)) noteByStone.set(v.subjectId, v.transcript);
  }

  return stones.map((s: LocalStone) => {
    const current = resolveCurrent(byStone.get(s.id) ?? []);
    const values: StoneRow['values'] = new Map();
    for (const [attr, f] of current) {
      values.set(attr, {
        text: f.valueText ?? null,
        num: f.valueNum == null ? null : Number(f.valueNum),
        sourceType: f.sourceType,
        confidence: f.confidence,
      });
    }
    const sup = supplierById.get(s.supplierId);
    const cert = certsByStone.get(s.id);
    return {
      id: s.id,
      kind: s.kind,
      status: s.status,
      no: s.publicNo ?? s.localNo,
      supplierId: s.supplierId,
      supplierName: sup?.companyName,
      booth: sup?.booth,
      where: sup ? whereIs(sup) || undefined : undefined,
      currencyDefault: currencyByPlace.get(s.exhibitionId),
      exhibitionId: s.exhibitionId,
      capturedAt: s.capturedAt ?? s.createdAt,
      values,
      note: noteByStone.get(s.id),
      hasCert: Boolean(cert?.any),
      certVerified: Boolean(cert?.verified),
    };
  });
}

/** Обложки для показанных камней. Грузим только то, что видно. */
export async function loadThumbs(ids: string[]): Promise<Record<string, string>> {
  const out: Record<string, string> = {};
  for (const id of ids) {
    const m = await db.media.where('subjectId').equals(id)
      .filter((x) => x.role === 'preview' || x.role === 'photo').first();
    const u = pictureUrl(m);
    if (u) out[id] = u;
  }
  return out;
}
